<template>
    <section
        ref="section"
        :class="['slice slice-faq', `slice-faq--${faqVariationModifier}`, {'slice-faq--hide-in-mobile': slice.primary.hideInMobile}]"
        aria-label="faq"
        :data-testid="isFaqWithImage ? 'slice-faq-with-image' : 'slice-faq-default'"
    >
        <div :class="['slice-content', `slice-faq--${faqVariationModifier}__content`, {'slice-faq--default__content--narrow': !isFaqWithImage}]">
            <PrismicRichText :field="slice.primary.title" class="slice-title" data-testid="slice-faq-title" />
            <PrismicRichText :field="slice.primary.description" class="slice-description" data-testid="slice-faq-description" />
            <slot name="image" />
            <div :class="['slice-faq__items', {'slice-faq__items--full-width': faqVariationModifier === 'default'}]">
                <MktFaqCollapsible
                    v-for="(item, index) in faqItems"
                    :id="asText(item.questionTitle)"
                    :key="`faq-item-${index}`"
                    class="slice-faq__item"
                >
                    <template #header>
                        <PrismicRichText :field="item.questionTitle" class="slice-faq__question-title" />
                    </template>
                    <template #content>
                        <PrismicRichText :field="item.answer" class="slice-faq__answer" />
                    </template>
                </MktFaqCollapsible>
            </div>
            <div v-if="isFilled.link(slice.primary.link)" class="slice-faq__link-container">
                <PrismicLink :field="slice.primary.link" class="slice-faq__link" data-component-source="faq">
                    {{ slice.primary.link.text }}
                </PrismicLink>
            </div>
        </div>
    </section>
</template>

<script setup lang="ts">
    import {computed, ref} from 'vue';
    import {asText, isFilled} from '@prismicio/helpers';
    import {PrismicLink, PrismicRichText} from '@prismicio/vue';
    import type {FaqSlice, FaqSliceDefault, FaqSliceFaqWithImage} from '@prismicio-types';
    import {MktFaqCollapsible} from '#components';
    import {useComponentTracking} from '#imports';

    const props = defineProps<{
        slice: FaqSlice;
        isFaqWithImage?: boolean;
    }>();

    const isFaqWithImage = computed(() => props.isFaqWithImage ?? false);

    const faqVariationModifier = computed(() => (isFaqWithImage.value ? 'with-image' : 'default'));

    const slice = computed(() => props.slice as FaqSliceDefault | FaqSliceFaqWithImage);
    const section = ref(null);

    const faqItems = computed(() => slice.value.primary.items ?? []);

    useComponentTracking(section);
</script>

<style lang="scss">
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/media-queries/media-queries' as mq;

    .slice-faq {
        &--default__content--narrow.slice-content {
            max-width: 730px;
            margin-left: auto;
            margin-right: auto;
        }

        &--default {
            &__content {
                font-family: var(--joy-font-family-base);
            }
        }

        &--with-image {
            &__content {
                font-family: var(--joy-font-family-base);
                display: grid;
                grid-template-columns: 31.25% 1fr;
                column-gap: var(--joy-core-spacing-12);
                row-gap: var(--joy-core-spacing-6);

                @include mq.screen_sm(max) {
                    display: flex;
                    flex-direction: column;
                    justify-content: center;
                    align-items: center;
                }
            }

            &__image {
                margin: var(--joy-core-spacing-8) auto 0;
                width: 100%;
                max-width: 640px;
                height: auto;
                object-fit: contain;
                object-position: top left;

                @include mq.screen_sm(max) {
                    order: 5;
                    margin-top: var(--joy-core-spacing-6);
                }
            }
        }
    }
</style>
